import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

interface StartupDescriptionProps {
  startupId: string;
  shortDescription: string;
  description: string;
  isOwner: boolean;
  onUpdate: () => void;
}

export const StartupDescription = ({
  startupId,
  shortDescription,
  description,
  isOwner,
  onUpdate,
}: StartupDescriptionProps) => {
  const [isEditingShort, setIsEditingShort] = useState(false);
  const [isEditingLong, setIsEditingLong] = useState(false);
  const [editedShortDescription, setEditedShortDescription] = useState(shortDescription);
  const [editedDescription, setEditedDescription] = useState(description);
  const { toast } = useToast();

  const handleSaveShortDescription = async () => {
    try {
      const { error } = await supabase
        .from('startups')
        .update({ short_description: editedShortDescription })
        .eq('id', startupId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Startup description updated successfully",
      });
      
      setIsEditingShort(false);
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update startup description",
        variant: "destructive"
      });
    }
  };

  const handleSaveDescription = async () => {
    try {
      const { error } = await supabase
        .from('startups')
        .update({ description: editedDescription })
        .eq('id', startupId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Startup description updated successfully",
      });
      
      setIsEditingLong(false);
      onUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update startup description",
        variant: "destructive"
      });
    }
  };

  return (
    <div>
      <div className="mb-4">
        {isEditingShort ? (
          <div className="flex items-center gap-2">
            <Textarea
              value={editedShortDescription}
              onChange={(e) => setEditedShortDescription(e.target.value)}
              className="bg-transparent border-flux-purple/20"
            />
            <Button
              size="icon"
              variant="ghost"
              onClick={handleSaveShortDescription}
              className="text-flux-purple hover:text-flux-purple/80"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-gray-400">{shortDescription}</p>
            {isOwner && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setIsEditingShort(true)}
                className="text-flux-purple hover:text-flux-purple/80"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      {isEditingLong ? (
        <div className="flex items-center gap-2">
          <Textarea
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            className="bg-transparent border-flux-purple/20 min-h-[200px]"
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={handleSaveDescription}
            className="text-flux-purple hover:text-flux-purple/80"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <p className="text-gray-300">{description}</p>
          {isOwner && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsEditingLong(true)}
              className="text-flux-purple hover:text-flux-purple/80"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};